/**
 * L2 (IndexedDB) store for whole SERVER PAGES — the half of the local cache the
 * `messages` store cannot serve (CYBORG-987).
 *
 * Agent DMs need nothing here: their rows are ordinary ledger `Message`s
 * (`channelId == null`, `fromId === cyboId`) that `messageBelongsToDm` already
 * accepts, so they reuse `dmScope` and the `messages` store unchanged. What has no
 * home is everything that is NOT a `Message`:
 *
 *   - an AGENT TIMELINE   (`cyborg:fetch_agent_timeline`) — `AgentTimelineItem`s;
 *   - a SESSION's WORK    (`cyborg:fetch_session_work`)   — the same envelope,
 *     session-scoped;
 *   - the CYBO-DM LEDGER window the cross-session merge pages back through, kept in
 *     the merge's OWN candidate shape (id/text/seq/createdAt/card) rather than as
 *     `Message`s, because that merge only ever sees a narrowed structural type and
 *     widening it has silently dropped fields twice already (see mergeMissingCyboDms).
 *
 * WHY IndexedDB AND NOT localStorage. localStorage is synchronous and shares one
 * ~5MB origin budget: a single agent timeline is up to 100 items whose tool-call rows
 * carry file diffs and command output, so a handful of conversations would both block
 * the main thread on every write and evict each other silently once the budget ran
 * out. This DB already exists, is async, is quota-aware, is LRU-swept, and — the
 * decisive one — is wiped as a whole when a different human signs in (cacheInit /
 * cacheClear), which localStorage is not. A new localStorage bucket would have been a
 * new place to leak one account's session transcripts to the next.
 *
 * Everything here is best-effort: a miss or ANY error resolves to null / a no-op so
 * the caller runs its existing network-first path unchanged.
 */

import { getDB, reqDone, txDone, STORES, INDEXES, type CachedPage } from "./idb.js";
import { cacheReadable } from "./read-gate.js";
import { isViewerCacheKeySegment } from "./local-owner.js";

/**
 * Key kinds. Deliberately NOT part of `scope.ts`'s `ScopeKind`: those keys index the
 * `messages` store, where `evictLegacyScopes` DELETES every row whose key doesn't
 * parse to the `ch`/`dm` shape and `getRecent` picks its content guard off the parsed
 * kind. Keeping these in their own store and their own namespace means neither can
 * ever mistake one for the other.
 */
// Every page kind is keyed by VIEWER too (CYBO-1846): what each read returns depends on who
// asks (an agent timeline is its initiator's, a ledger window is this person's DM), so a
// shared browser never paints one person's page to another, even if the sign-in wipe
// (cacheInit) never ran. `viewer` is viewerCacheKeySegment(email), never an address; with none,
// callers cache nothing.
export function agentTimelineKey(workspaceId: string, agentId: string, viewer: string): string {
  return `${workspaceId}:ag:${viewer}:${agentId}`;
}
export function sessionWorkKey(workspaceId: string, sessionId: string, viewer: string): string {
  return `${workspaceId}:sw:${viewer}:${sessionId}`;
}
const LEDGER_NS = ":dml:";
export function cyboDmLedgerKey(workspaceId: string, cyboId: string, viewer: string): string {
  return `${workspaceId}${LEDGER_NS}${viewer}:${cyboId}`;
}
/** Is this key a cybo-DM ledger window? The only page kind holding `Message` ids. */
export function isCyboDmLedgerKey(key: string): boolean {
  return key.includes(LEDGER_NS) && isViewerKeyedPageKey(key);
}
/** `<ws>:<kind>:<viewer>:<id>`: anything else predates CYBO-1846 and is evicted at sign-in. */
function isViewerKeyedPageKey(key: string): boolean {
  const [, , viewer = "", id] = key.split(":", 4);
  return isViewerCacheKeySegment(viewer) && !!id;
}

/**
 * How many pages we keep. One row is one conversation's newest page, so this is
 * "the last 40 things you opened" — comfortably more than a session's worth of
 * navigation, and small enough that the whole store stays far under quota.
 * ponytail: fixed cap, swept at init only. Sweep on write if it ever overruns.
 */
const MAX_PAGES = 40;

/** Read one cached page. Null on a miss, on an error, or when IDB is unavailable. */
export async function getPage(key: string): Promise<CachedPage | null> {
  const dbp = getDB();
  if (!dbp || !(await cacheReadable())) return null;
  try {
    const db = await dbp;
    const tx = db.transaction(STORES.PAGES, "readonly");
    const row = (await reqDone(tx.objectStore(STORES.PAGES).get(key))) as CachedPage | undefined;
    // A row whose own key doesn't match the slot it came back from is never trusted —
    // the same "the row's OWN fields must say it belongs here" rule scope.ts applies
    // to messages, which is cheap to repeat and impossible to regress.
    if (!row || row.key !== key || !Array.isArray(row.items)) return null;
    return row;
  } catch {
    return null; // intentional: a cache read never breaks the caller's network path.
  }
}

/**
 * Write one page. Fire-and-forget: a quota failure evicts the oldest pages and retries
 * once, then gives up — the network answer is the source of truth either way.
 *
 * An EMPTY page is never written. Zero rows is the signature of a read-source miss (a
 * daemon that couldn't answer), not of an empty conversation, and persisting it would
 * pin exactly that miss as the next cold paint.
 */
export async function putPage(page: {
  key: string;
  items: unknown[];
  hasOlder?: boolean;
  olderCursor?: string | null;
  maxSeq?: number;
  life?: string;
}): Promise<void> {
  if (page.items.length === 0) return;
  const dbp = getDB();
  if (!dbp) return;
  const row: CachedPage = {
    key: page.key,
    items: page.items,
    hasOlder: page.hasOlder ?? false,
    olderCursor: page.olderCursor ?? null,
    maxSeq: typeof page.maxSeq === "number" ? page.maxSeq : null,
    ...(typeof page.life === "string" ? { life: page.life } : {}),
    cachedAt: Date.now(),
  };
  const writeOnce = async () => {
    const db = await dbp;
    const tx = db.transaction(STORES.PAGES, "readwrite");
    tx.objectStore(STORES.PAGES).put(row);
    await txDone(tx);
  };
  try {
    await writeOnce();
  } catch (err) {
    if ((err as { name?: string })?.name !== "QuotaExceededError") return;
    await evictOldPages(MAX_PAGES / 2);
    try {
      await writeOnce();
    } catch {
      // intentional: second failure drops the page; the cache is best-effort.
    }
  }
}

/** Drop one key (unused today; the sweep is what bounds the store). */
export async function deletePage(key: string): Promise<void> {
  const dbp = getDB();
  if (!dbp) return;
  try {
    const db = await dbp;
    const tx = db.transaction(STORES.PAGES, "readwrite");
    tx.objectStore(STORES.PAGES).delete(key);
    await txDone(tx);
  } catch {
    // intentional: best-effort delete; a surviving row is re-validated on read.
  }
}

/**
 * Apply a message mutation to ONE cached ledger window's items. Returns the new item
 * list, or null when this window doesn't hold the message (the caller then skips the
 * write). Pure, so the rule is unit-testable without an IndexedDB.
 *
 * `text === null` is a DELETE and drops the row. An edit that empties the text drops it
 * too: the merge that consumes these rows already refuses empty-text rows on the fetched
 * side (an image-only reply persists with `text: ""`), and a blank bubble is worse than
 * an omitted one.
 */
export function applyLedgerMutation(
  items: readonly unknown[],
  messageId: string,
  text: string | null,
): unknown[] | null {
  if (!items.some((it) => (it as { id?: unknown } | null)?.id === messageId)) return null;
  const drop = text === null || text.trim() === "";
  const out: unknown[] = [];
  for (const it of items) {
    if ((it as { id?: unknown } | null)?.id !== messageId) out.push(it);
    else if (!drop) out.push({ ...(it as object), text });
  }
  return out;
}

/** A row's position in the DM's order — the composite `getDmMessages` sorts and cursors by. */
export interface LedgerPosition {
  createdAt: number;
  seq: number;
}

/** Strictly earlier in that order. */
function isOlder(a: LedgerPosition, b: LedgerPosition): boolean {
  return a.createdAt !== b.createdAt ? a.createdAt < b.createdAt : a.seq < b.seq;
}

/** A row whose position is unusable (a legacy/corrupt cached row) can never be placed. */
function hasPosition(p: LedgerPosition): boolean {
  return Number.isFinite(p.createdAt) && Number.isFinite(p.seq);
}

/**
 * What a walk can PROVE about the two ends of the range it fetched. Only the caller
 * knows, so both flags are its to earn (see reconcileLedgerWindow).
 */
export interface LedgerCoverage {
  /** Page 0 was an un-cursored TAIL read: nothing on the server sits above it. */
  reachesNewest: boolean;
  /** A page answered `hasMore: false`: nothing on the server sits below it. */
  reachesOldest: boolean;
}

/**
 * Reconcile a cached cybo-DM ledger window against the range the fetched pages
 * DEMONSTRABLY COVERED — the pages-store twin of `messages.ts`'s `reconcileScope`, and
 * pure like `applyLedgerMutation` so the rule is testable without an IndexedDB
 * (CYBORG-979).
 *
 * WHY IT EXISTS. `patchLedgerMessage` above only covers a delete this client was
 * CONNECTED for. One that happened while it was away never broadcasts here, and
 * re-fetching cannot remove a row that is ABSENT from the response — `getDmMessages`
 * filters soft-deleted rows out (`isNull(deletedAt)`) — so the cached copy survives the
 * walk, merges, and is re-persisted on every open, effectively forever in a quiet DM.
 *
 * THE COVERAGE RULE — the whole point of this function, and the hazard that made
 * CYBORG-992 stop at the live delete. A cached row may be dropped ONLY when the fetched
 * pages demonstrably covered its position and did not contain it. A page is bounded (50
 * rows, a 3-page budget) and the cached window is bounded DIFFERENTLY (150 rows), so
 * "the fetch didn't return it" proves nothing by itself: dropping on that alone deletes
 * rows that merely sat OUTSIDE the fetched range — real messages, gone from the user's
 * history. Anything outside the proven-covered range is therefore left alone, and when
 * coverage cannot be proven at all the row STAYS. An occasional stale row is a bug; a
 * wrongly-dropped message is data loss.
 *
 * POSITION IS `(createdAt, seq)`, the composite the server orders AND cursors by — not
 * `seq` alone. The two agree in the common case, but the slice the server returns is
 * cut by the composite, so only the composite proves a row sat inside that slice.
 *
 * `fetched` is every row the pages returned, HUMAN ROWS INCLUDED: each page is a
 * contiguous slice of the whole DM and every page after the first is cursored off the
 * previous page's oldest row, so their union is one contiguous range whose extremes are
 * exactly what was covered. No rows at all (a first-page failure, an empty ledger) is no
 * coverage at either end, so nothing is dropped.
 *
 * THE RANGE IS NOT ALWAYS CLOSED. A delete at either EXTREME of the window sits outside
 * [oldest fetched, newest fetched] and would survive forever, which in a short agent DM
 * means its first or last message. `coverage` is how the caller opens an end it can
 * actually prove; required, so an end is never opened by default.
 */
export function reconcileLedgerWindow<T extends LedgerPosition & { id: string }>(
  rows: readonly T[],
  fetched: readonly (LedgerPosition & { id: string })[],
  coverage: LedgerCoverage,
): T[] {
  const fetchedIds = new Set<string>();
  let oldest: LedgerPosition | null = null;
  let newest: LedgerPosition | null = null;
  for (const m of fetched) {
    fetchedIds.add(m.id);
    if (!hasPosition(m)) continue;
    if (!oldest || isOlder(m, oldest)) oldest = m;
    if (!newest || isOlder(newest, m)) newest = m;
  }
  if (!oldest || !newest) return [...rows];
  const from = oldest;
  const to = newest;
  return rows.filter((r) => {
    if (fetchedIds.has(r.id) || !hasPosition(r)) return true;
    if (isOlder(r, from)) return !coverage.reachesOldest;
    if (isOlder(to, r)) return !coverage.reachesNewest;
    return false;
  });
}

/**
 * Mirror a `delete_message` / `edit_message` into the cached cybo-DM LEDGER windows —
 * the pages-store twin of what `patchById`/`markDeleted` do to the `messages` store
 * (CYBORG-992).
 *
 * Without this the ledger window is the one cache that only ever GROWS: the merge that
 * reads it unions cached rows with fetched ones, and a soft-deleted row is filtered out
 * server-side (`getDmMessages` has `isNull(deletedAt)`) yet stays in the cached window
 * forever — so it resurrected on every open. The agent-timeline and session-work pages
 * need nothing here: a successful fetch replaces those wholesale, and their items are
 * timeline rows, not messages.
 *
 * ponytail: reads every page row and filters in JS rather than driving a key cursor —
 * bounded by MAX_PAGES, and a message delete is a rare, user-driven event. Narrow the
 * read if the pages store ever grows a bigger cap.
 */
export async function patchLedgerMessage(messageId: string, text: string | null): Promise<void> {
  const dbp = getDB();
  if (!dbp) return;
  try {
    const db = await dbp;
    const tx = db.transaction(STORES.PAGES, "readwrite");
    const store = tx.objectStore(STORES.PAGES);
    const rows = (await reqDone(store.getAll())) as CachedPage[];
    for (const row of rows) {
      if (!row || !isCyboDmLedgerKey(row.key) || !Array.isArray(row.items)) continue;
      const items = applyLedgerMutation(row.items, messageId, text);
      if (items) store.put({ ...row, items });
    }
    await txDone(tx);
  } catch {
    // intentional: best-effort, like every other cache mutation. A surviving row is
    // re-validated by the next fetch, which now wins over its cached twin.
  }
}

/**
 * Drop the N least-recently-written pages. Keys first, then deletes: the index hands
 * them back already in `cachedAt` ASC order, and the store is capped, so a key list is
 * both cheaper to reason about and cheaper than driving a cursor by hand.
 */
export async function evictOldPages(count: number): Promise<void> {
  if (count <= 0) return;
  const dbp = getDB();
  if (!dbp) return;
  try {
    const db = await dbp;
    const tx = db.transaction(STORES.PAGES, "readwrite");
    const store = tx.objectStore(STORES.PAGES);
    const oldestFirst = (await reqDone(store.index(INDEXES.CACHED).getAllKeys())) as IDBValidKey[];
    for (const key of oldestFirst.slice(0, count)) store.delete(key);
    await txDone(tx);
  } catch {
    // intentional: best-effort sweep; the store is re-swept on the next init.
  }
}

/** Run at init (cacheInit). No-op under the cap. */
/** Delete every page whose key is not viewer-keyed (S1, CYBO-1846): one-way, a cold read. */
export async function evictLegacyPages(): Promise<void> {
  const dbp = getDB();
  if (!dbp) return;
  try {
    const db = await dbp;
    const tx = db.transaction(STORES.PAGES, "readwrite");
    const store = tx.objectStore(STORES.PAGES);
    const keys = (await reqDone(store.getAllKeys())) as IDBValidKey[];
    for (const key of keys) if (!isViewerKeyedPageKey(String(key))) store.delete(key);
    await txDone(tx);
  } catch {
    // intentional: best-effort sweep; a legacy key is never built, so it is never read.
  }
}

export async function evictPagesOverCap(): Promise<void> {
  const dbp = getDB();
  if (!dbp) return;
  try {
    const db = await dbp;
    const tx = db.transaction(STORES.PAGES, "readonly");
    const total = (await reqDone(tx.objectStore(STORES.PAGES).count())) as number;
    if (total <= MAX_PAGES) return;
    await evictOldPages(total - MAX_PAGES / 2);
  } catch {
    // intentional: best-effort sweep; nothing downstream depends on it running.
  }
}
